import { API } from '../Client';
import { CreateSubscriptionResponse, SubscriptionPlan } from './types';

/** Subscription item as returned by GET /subscriptions */
export type SubscriptionItem = {
  id: string;
  status: string;
  [key: string]: unknown;
};

export type HasActiveSubscriptionResponse = {
  hasActive: boolean;
};

const ACTIVE_STATUS = 'Active';

export namespace SubscriptionService {
/**
   * Fetches all subscriptions for the current user (GET /subscriptions).
   */
  export async function getSubscriptions(
    isChild = false
  ): Promise<SubscriptionItem[]> {
    const { data } = await API.get<SubscriptionItem[]>(
      '/subscriptions',
      { params: { isChild } }
    );
    return Array.isArray(data) ? data : [];
  }

  /**
   * Returns whether the current user has an active subscription.
   * Uses GET /subscriptions and checks if any subscription has status Active.
   * Use for feature gating (e.g. Learn menu) independent of trading permissions.
   *
   * @param isChild - When true, checks subscription as the child (teen) context
   * @returns { hasActive: boolean }
   */
  export async function hasActiveSubscription(
    isChild = false
  ): Promise<HasActiveSubscriptionResponse> {
    const subscriptions = await getSubscriptions(isChild);
    const hasActive =
      subscriptions.some((s) => s.status === ACTIVE_STATUS) ?? false;
    return { hasActive };
  }

  /**
   * Fetches all subscription plans (for display on plans page).
   */
  export async function getPlans(): Promise<SubscriptionPlan[]> {
    const res = await API.get<SubscriptionPlan[]>('/subscriptions/plans');
    return res?.data ?? [];
  }

  /**
   * Creates an inactive subscription for the current user. Caller must then
   * add a payment method and activate (e.g. via app or Stripe flow).
   */
  export async function createSubscription(planId: string): Promise<CreateSubscriptionResponse> {
    const res = await API.post<CreateSubscriptionResponse>('/subscriptions', { planId });
    return res?.data;
  }

  /**
   * Gets Stripe Billing Portal URL for subscribe flow. User is redirected to
   * Stripe's pages to subscribe or manage subscription (same as app).
   * Requires auth. If returnUrl is provided, Stripe will redirect there after.
   */
  export async function getSubscribePortalUrl(returnUrl?: string): Promise<string> {
    const res = await API.get<string>('/subscriptions/create-portal-session-subscribe', {
      params: returnUrl ? { returnUrl } : {},
    });
    return res?.data;
  }
}